import React, { useState, useEffect } from 'react';
import { get } from '../utils/api';
import { useAuth } from '../context/AuthContext';

export default function AIBriefing() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [briefing, setBriefing] = useState('');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [lastGenerated, setLastGenerated] = useState(null);

  if (user?.role !== 'super_admin') {
    return <div className="empty-state"><h3>Access Restricted</h3><p>The AI Briefing is only available to the Managing Director.</p></div>;
  }

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await get('/ai-briefing/briefing');
      setData(result.data);
      return result.data;
    } catch (err) {
      setError('Failed to fetch business data: ' + err.message);
      return null;
    } finally { setLoading(false); }
  };

  const generateBriefing = async (forceNew=false) => {
    setGenerating(true);
    setError('');

    // Check if we already have today's briefing cached
    if (!forceNew) {
      try {
        const token = localStorage.getItem('bb_token');
        const res = await fetch('/api/ai-briefing/today', {
          headers: { Authorization: 'Bearer '+token }
        });
        const data = await res.json();
        if (data.text) {
          setBriefing(data.text);
          setLastGenerated(new Date());
          setGenerating(false);
          return;
        }
      } catch(e) {}
    }
    
    const bizData = await fetchData();
    if (!bizData) { setGenerating(false); return; }

    try {
      const fmt = n => '₦' + Number(n||0).toLocaleString('en-NG');
      
      const prompt = `You are a Chief of Staff for Bloans Creations Ltd, a jute bag manufacturing and luxury gift packaging company in Ibadan, Nigeria. The Managing Director is Doris Gideon-Ayantola.

Write a professional, warm and concise morning executive briefing for Doris based on the following real business data from BloansBooks. Write in plain English — no bullet points in the opening, use a natural flowing style. Be direct about problems. Celebrate wins. Use Nigerian Naira (₦) for all amounts.

TODAY: ${bizData.date}

YESTERDAY'S ACTIVITY:
- Invoices created: ${bizData.yesterday.invoices_count} totalling ${fmt(bizData.yesterday.invoices_total)}
- Payments received: ${bizData.yesterday.payments_count} totalling ${fmt(bizData.yesterday.payments_total)}
${bizData.yesterday.payments_details ? '- Payment details: ' + bizData.yesterday.payments_details : '- No payments received yesterday'}
- Job orders updated: ${bizData.yesterday.jobs_updated} (${bizData.yesterday.jobs_completed} completed, ${bizData.yesterday.jobs_dispatched} dispatched)

FINANCIAL POSITION:
- Revenue this month: ${fmt(bizData.finance.revenue_this_month)}
- Revenue last month: ${fmt(bizData.finance.revenue_last_month)}
- Petty cash balance: ${fmt(bizData.finance.petty_cash_balance)}
- Top outstanding customers: ${bizData.finance.top_outstanding.map(c => c.name + ' owes ' + fmt(c.total_owing)).join(', ') || 'None'}

PENDING APPROVALS:
- Expenses awaiting approval: ${bizData.alerts.pending_expenses}
- Inventory adjustments: ${bizData.alerts.pending_inventory}
- Payroll entries: ${bizData.alerts.pending_payroll}
- Invoice discounts: ${bizData.alerts.pending_discounts}
- Leave requests: ${bizData.alerts.pending_leave}
- Dispatch overrides: ${bizData.alerts.pending_dispatch}

OVERDUE INVOICES: ${bizData.alerts.overdue_invoices_count} invoices totalling ${fmt(bizData.alerts.overdue_invoices_total)}

LOW STOCK MATERIALS:
${bizData.alerts.low_stock_items.length > 0 ? bizData.alerts.low_stock_items.map(m => m.name + ': ' + m.current_stock + ' ' + m.unit_of_measure + ' (reorder at ' + m.reorder_level + ')').join('\n') : 'No low stock items'}

OVERDUE JOB ORDERS:
${bizData.alerts.overdue_jobs.length > 0 ? bizData.alerts.overdue_jobs.map(j => j.job_number + ' for ' + j.customer_name + ' — ' + j.days_overdue + ' days overdue (' + j.status + ')').join('\n') : 'No overdue job orders'}

OVERDUE TASKS:
${bizData.alerts.overdue_tasks.length > 0 ? bizData.alerts.overdue_tasks.map(t => t.title + ' assigned to ' + t.assigned_to_name + ' (due ' + t.due_date + ')').join('\n') : 'No overdue tasks'}

BILLS DUE IN 3 DAYS:
${bizData.alerts.bills_due_soon.length > 0 ? bizData.alerts.bills_due_soon.map(b => b.bill_number + ' — ' + b.supplier_name + ': ' + fmt(b.balance_due) + ' due ' + b.due_date).join('\n') : 'No bills due in the next 3 days'}

DELIVERIES:
- Yesterday: ${bizData.deliveries?.yesterday_count || 0} deliveries updated (${bizData.deliveries?.yesterday_delivered || 0} delivered, ${bizData.deliveries?.yesterday_out_for_delivery || 0} out for delivery, ${bizData.deliveries?.yesterday_picked_up || 0} picked up)
- Active deliveries in transit: ${bizData.deliveries?.active_deliveries?.length || 0}
${bizData.deliveries?.deliveries_with_storage_fees?.length > 0 ? '- ⚠ Deliveries accruing storage fees: ' + bizData.deliveries.deliveries_with_storage_fees.map(d => d.customer_name + ' (' + d.billable_days + ' billable days)').join(', ') : '- No deliveries accruing storage fees'}
${bizData.deliveries?.active_deliveries?.length > 0 ? '- Active: ' + bizData.deliveries.active_deliveries.map(d => d.customer_name + ' [' + d.status.replace(/_/g,' ') + ']').join(', ') : ''}

Structure your briefing as:
1. A warm greeting with today's date
2. "What happened yesterday" — 2-3 sentences summarising activity
3. "What needs your attention today" — list the most important issues requiring action, prioritised by urgency
4. "Financial snapshot" — brief summary of money position
5. "Good news" — any positive highlights worth celebrating (if any)
6. End with one short motivational closing line

Keep the whole briefing under 400 words. Be specific with numbers. Sound like a trusted advisor, not a robot.`;

      // Call backend AI route
      const token = localStorage.getItem('bb_token');
      const response = await fetch('/api/ai-briefing/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer '+token },
        body: JSON.stringify({ prompt })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error);
      setBriefing(result.text);
      setLastGenerated(new Date());
    } catch (err) {
      setError('Failed to generate briefing: ' + err.message);
    } finally { setGenerating(false); }
  };

  useEffect(() => { generateBriefing(); }, []);

  return (
    <div>
      <div className="flex-between mb-lg">
        <div>
          <h2 className="page-title">AI Morning Briefing</h2>
          <p className="text-muted text-sm" style={{marginTop:4}}>
            Your daily executive summary powered by Claude AI
          </p>
        </div>
        <button className="btn btn-primary" onClick={()=>generateBriefing(true)}
          disabled={generating||loading}>
          {generating ? '✨ Generating...' : '↻ Refresh Briefing'}
        </button>
      </div>

      {/* Header card */}
      <div style={{background:'linear-gradient(135deg, #3B2A21 0%, #556B2F 100%)',
        borderRadius:12,padding:'24px 28px',marginBottom:20,color:'#F2EFE7'}}>
        <div style={{fontSize:12,color:'rgba(242,239,231,.5)',marginBottom:4,letterSpacing:'.08em',textTransform:'uppercase'}}>
          Executive Briefing
        </div>
        <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.6rem',color:'#A8893D',fontWeight:600}}>
          {new Date().toLocaleDateString('en-NG',{weekday:'long',day:'numeric',month:'long',year:'numeric'})}
        </div>
        {lastGenerated && (
          <div style={{fontSize:11,color:'rgba(242,239,231,.4)',marginTop:8}}>
            Last generated: {lastGenerated.toLocaleTimeString('en-NG')}
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div style={{background:'#FDEAEA',border:'1px solid #F5B8B8',borderRadius:8,
          padding:'12px 16px',marginBottom:16,color:'#8F2E24',fontSize:13}}>
          {error}
        </div>
      )}

      {/* Loading */}
      {(loading || generating) && (
        <div style={{background:'#fff',border:'1px solid var(--border-light)',borderRadius:10,
          padding:40,textAlign:'center'}}>
          <div className="spinner" style={{margin:'0 auto 16px'}}></div>
          <div style={{fontSize:13,color:'#8A8A8A'}}>
            {loading ? 'Fetching business data...' : '✨ Claude is writing your briefing...'}
          </div>
        </div>
      )}

      {/* Briefing content */}
      {briefing && !generating && (
        <div style={{background:'#fff',border:'1px solid var(--border-light)',
          borderRadius:10,overflow:'hidden'}}>
          <div style={{background:'#F2EFE7',padding:'12px 20px',borderBottom:'1px solid var(--border-light)',
            display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <span style={{fontSize:12,color:'#556B2F',fontWeight:600}}>✨ Generated by Claude AI</span>
            <span style={{fontSize:10,color:'#8A8A8A',marginLeft:8}}>(Cached — refreshes daily)</span>
            <button className="btn btn-ghost btn-sm" style={{fontSize:11}}
              onClick={()=>window.print()}>🖨 Print</button>
          </div>
          <div style={{padding:'28px 32px'}}>
            {briefing.split('\n').map((line, i) => {
              if (!line.trim()) return <div key={i} style={{height:12}}></div>;
              // Section headers
              if (line.startsWith('**') && line.endsWith('**')) {
                return (
                  <div key={i} style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.1rem',
                    fontWeight:700,color:'#3B2A21',marginTop:20,marginBottom:8,
                    borderBottom:'1px solid #F2EFE7',paddingBottom:6}}>
                    {line.replace(/\*\*/g,'')}
                  </div>
                );
              }
              // Bullet points
              if (line.startsWith('- ') || line.startsWith('• ')) {
                return (
                  <div key={i} style={{display:'flex',gap:8,marginBottom:6,fontSize:14,
                    color:'#3B2A21',lineHeight:1.6,paddingLeft:8}}>
                    <span style={{color:'#A8893D',flexShrink:0}}>•</span>
                    <span>{line.replace(/^[-•]\s/,'')}</span>
                  </div>
                );
              }
              // Regular text
              return (
                <p key={i} style={{fontSize:14,color:'#3B2A21',lineHeight:1.7,
                  marginBottom:8,fontFamily:'Georgia,serif'}}>
                  {line}
                </p>
              );
            })}
          </div>
        </div>
      )}

      {/* Quick stats below briefing */}
      {data && !generating && (
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))',
          gap:10,marginTop:16}}>
          {[
            { label:'Pending Approvals', value: Object.values(data.alerts).filter(v=>typeof v==='number').reduce((a,b)=>a+b,0), color:'#8B5E0A', bg:'#FFF3DC' },
            { label:'Overdue Invoices', value: data.alerts.overdue_invoices_count, color:'#8F2E24', bg:'#FDEAEA' },
            { label:'Low Stock Items', value: data.alerts.low_stock_items.length, color:'#8F2E24', bg:'#FDEAEA' },
            { label:'Overdue Jobs', value: data.alerts.overdue_jobs.length, color:'#8F2E24', bg:'#FDEAEA' },
            { label:'Bills Due Soon', value: data.alerts.bills_due_soon.length, color:'#8B5E0A', bg:'#FFF3DC' },
          ].map(tile => (
            <div key={tile.label} style={{background:tile.bg,borderRadius:8,padding:'10px 14px',
              border:'1px solid '+tile.color+'33'}}>
              <div style={{fontSize:9,fontWeight:600,textTransform:'uppercase',
                letterSpacing:'.08em',color:tile.color,marginBottom:4}}>{tile.label}</div>
              <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.4rem',
                fontWeight:700,color:tile.color}}>{tile.value}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
